import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import type { ApiError } from '../utils/api-helper';

// Idle-Tracking: nach IDLE_THRESHOLD_MS ohne Maus/Tastatur gilt der Nutzer als
// abwesend. Der separate Heartbeat unten (nicht die Auth-Query selbst!) verlängert
// die Sliding-Session dann nicht mehr künstlich weiter — die Session läuft nach
// ihrem regulären Timeout normal ab.
const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
const HEARTBEAT_INTERVAL_MS = 4 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

let lastActivityAt = Date.now();
let activityTrackingStarted = false;

function ensureActivityTracking() {
  if (activityTrackingStarted || typeof window === 'undefined') return;
  activityTrackingStarted = true;
  const markActive = () => { lastActivityAt = Date.now(); };
  ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
}

async function fetchAuthUser() {
  const response = await fetch('/routes/auth/me', { credentials: 'include' });
  if (!response.ok) {
    const err: ApiError = new Error('Authentication failed');
    err.status = response.status;
    throw err;
  }
  return response.json();
}

// Eigenständiger Heartbeat, entkoppelt von der Auth-Query: läuft nur während der Tab
// sichtbar ist und der Nutzer aktiv war, und verlängert per Seiteneffekt die
// Sliding-Session serverseitig. Bewusst getrennt von useAuthUser(), damit die
// Idle-Heuristik NIE den echten Auth-Check (Mount / Tab-Fokus) verfälschen kann —
// genau das führte vorher dazu, dass ein nach langer Abwesenheit abgelaufener
// Login erst nach einem manuellen Seiten-Reload erkannt wurde.
function useSessionHeartbeat() {
  const queryClient = useQueryClient();
  useEffect(() => {
    const id = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastActivityAt > IDLE_THRESHOLD_MS) return;
      // Über queryClient.fetchQuery statt direktem fetchAuthUser(), damit ein 401
      // hier denselben globalen QueryCache-onError-Handler durchläuft wie jede andere
      // Query (siehe handleAuthError in AppWrapper.tsx) und zum Login umleitet, statt
      // in einem lokalen .catch(() => {}) zu verschwinden. staleTime: 0 erzwingt einen
      // echten Server-Roundtrip bei jedem Tick, unabhängig vom Cache-Zustand der
      // useAuthUser()-Query — sonst könnte fetchQuery die Anfrage anhand des globalen
      // Default-staleTime (5 Min.) überspringen und der Heartbeat liefe komplett leer.
      queryClient.fetchQuery({ queryKey: queryKeys.authUser, queryFn: fetchAuthUser, staleTime: 0 }).catch(() => {});
    }, HEARTBEAT_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [queryClient]);
}

export const useAuthUser = () => {
  ensureActivityTracking();
  useSessionHeartbeat();

  return useQuery({
    queryKey: queryKeys.authUser,
    queryFn: fetchAuthUser,
    retry: false,
    staleTime: 0,                    // Bei jeder SPA-Navigation Auth neu prüfen
    gcTime: 10 * 60 * 1000,          // Gecachte Daten behalten um Loading-Spinner zu vermeiden
    refetchOnWindowFocus: true,      // Immer ein echter Server-Check bei Tab-Rückkehr (überschreibt globales false)
  });
};
